import { getClients } from "@api/getClients.js";
import changePasswordMutation from "@api/mutations/changePassword.graphql";
import confirmForgotPasswordMutation from "@api/mutations/confirmForgotPassword.graphql";
import confirmSignUpMutation from "@api/mutations/confirmSignUp.graphql";
import editMeMutation from "@api/mutations/editMe.graphql";
import forgotPasswordMutation from "@api/mutations/forgotPassword.graphql";
import isCustomerMigratedQuery from "@api/mutations/isCustomerMigrated.graphql";
import resendConfirmationCodeMutation from "@api/mutations/resendConfirmationCode.graphql";
import signInWithGoogle from "@api/mutations/signInWithGoogle.graphql";
import signUp from "@api/mutations/signUp.graphql";
import getMe from "@api/queries/getMe.graphql";
import type { authenticationModalComponentMap } from "@components/auth-modal/authenticationModalComponentMap.js";
import * as Sentry from "@sentry/browser";
import type { ComponentProps } from "svelte";
import { AuthenticationModalKind } from "@core/schema/AuthenticationModalKind.js";
import type {
	Customer,
	CustomerCreateB2CInput,
	CustomerEditMeInput,
	CustomerSignInWithGoogleInput,
} from "@core/schema/Customer.js";
import type { Locale } from "@core/schema/Locale.js";
import { getOAuthPath } from "@core/schema/paths/getOAuthPath.js";
import type { Me } from "@core/schema/public/Me.js";
import type { Complete } from "@core/utils/Complete.js";
import type { LoginResponse } from "@core/utils/aws/cognito/LoginResponse.js";
import { StorageState } from "@core/utils/svelte/StorageState.svelte.js";
import type { GlobalSettings } from "../contexts/GlobalSettings.js";
import type { GlobalTemplateProps } from "../contexts/GlobalTemplateProps.js";
import { destinationCountryCookieName } from "../destinationCountryCookieName.js";
import { pricingLevelSecretKeyCookieName } from "../pricingLevelSecretKeyCookieName.js";
import type { AuthChannel } from "./AuthChannel.js";
import { initSentry } from "./initSentry.js";
import { setCookie } from "./setCookie.js";

class WebGlobalAuthentication {
	// We store customerId more persistently than customer data, as the details can get outdated.
	// Note that reload actually does not clear session storage, the storage will be kept as long as the tab is open.
	private readonly customerId = new StorageState<string | null>("local", "customerId", null);
	private readonly savedCustomer = new StorageState<Customer | null>("session", "customer", null);
	public readonly signedIn = $derived(this.customerId.value !== null);
	private readonly cognitoOAuthState = new StorageState<string | null>("local", "cognito-oauth-state", null);
	private currentModalKind = $state<AuthenticationModalKind>();
	private settings?: GlobalSettings;
	private globalTemplateProps?: GlobalTemplateProps;
	public readonly loggedCustomer = $derived(this.savedCustomer.value);
	public readonly authenticationModalKind = $derived(this.currentModalKind);

	public readonly loaded = $derived(
		this.savedCustomer.loaded && this.customerId.loaded && this.cognitoOAuthState.loaded,
	);

	public async openAuthenticationModal<K extends AuthenticationModalKind>(
		kind: K,
		props: ComponentProps<(typeof authenticationModalComponentMap)[K]>,
	): Promise<void> {
		if (this.settings) {
			await initSentry(this.settings);
		}

		Sentry.setContext("authModal", { kind });

		this.currentModalKind = kind;
		this.props = props;
	}

	public closeAuthenticationModal(): void {
		Sentry.setContext("authModal", null);

		this.currentModalKind = undefined;
		this.props = {};
	}

	public props = $state({});

	private async getClients(): Promise<ReturnType<typeof getClients>> {
		if (!this.settings) {
			throw new Error("Auth not initialized");
		}
		return await getClients(this.settings);
	}

	private async setToCustomer(customer: Customer | null): Promise<void> {
		if (customer) {
			this.customerId.value = customer.id;
			this.savedCustomer.value = customer;
			const [defaultAddress] = customer.deliveryAddresses;
			if (defaultAddress) {
				await setCookie(destinationCountryCookieName, defaultAddress.country);
			}
		} else {
			this.customerId.value = null;
			this.savedCustomer.value = null;
		}
	}

	private async setToMe(me: Me | null): Promise<void> {
		await this.setToCustomer(me?.customer ?? null);
		await setCookie(pricingLevelSecretKeyCookieName, me?.b2bPricingLevelSecretKey ?? null);
	}

	public async init(globalSettings: GlobalSettings, globalTemplateProps: GlobalTemplateProps): Promise<void> {
		this.settings = globalSettings;
		this.globalTemplateProps = globalTemplateProps;

		if (this.customerId.value === null && this.savedCustomer.value) {
			this.customerId.value = this.savedCustomer.value.id;
		}

		if (!this.savedCustomer.value && this.customerId.value) {
			const { graphQL } = await this.getClients();
			try {
				const me = await graphQL.query<Me>({ query: getMe });
				await this.setToMe(me ?? null);
			} catch {
				await this.setToMe(null);
			}
		}
	}

	public async initiateGoogleSignIn(locale: Locale, broadcastChannel: AuthChannel): Promise<void> {
		if (!this.settings) {
			throw new Error("Auth not initialized");
		}

		const { cognito } = await this.getClients();
		this.cognitoOAuthState.value = crypto.randomUUID();
		const url = cognito.getProviderLoginUrl(
			this.cognitoOAuthState.value,
			this.settings.WEB_URL_OR_LOCAL + getOAuthPath(locale),
		);

		const reload = await new Promise<boolean>((resolve, reject) => {
			const popup = window.open(url, "_blank", "width=500,height=600");
			if (popup === null) {
				reject(new Error("Popup blocked"));
				return;
			}

			const abortController = new AbortController();
			const listenForAuthModalChange = $effect.root(() => {
				$effect(() => {
					if (this.currentModalKind === undefined) {
						listenForAuthModalChange();
						resolve(false);
					}
				});

				const interval = window.setInterval(() => {
					if (popup.closed) {
						listenForAuthModalChange();
						reject(new Error("Popup closed"));
					}
				}, 500);

				window.addEventListener(
					"beforeunload",
					() => {
						popup.close();
					},
					{ signal: abortController.signal },
				);

				return (): void => {
					popup.close();
					clearInterval(interval);
					abortController.abort();
				};
			});

			broadcastChannel.addEventListener(
				"message",
				(event): void => {
					if (event.data.state !== this.cognitoOAuthState.value) {
						return;
					}

					listenForAuthModalChange();

					if (event.data.kind === "error") {
						reject(event.data.error);
					} else {
						resolve(true);
					}
				},
				{ signal: abortController.signal },
			);
		});
		this.closeAuthenticationModal();
		if (reload) {
			this.reloadAfterSignIn();
		}
	}

	public async completeGoogleSignIn(
		// We omit these because they are exchanged from the code in CognitoClient
		input: Omit<CustomerSignInWithGoogleInput, "fullName" | "email">,
		broadcastChannel: AuthChannel,
		code: string,
		state: string,
		redirectUri: string,
	): Promise<void> {
		try {
			if (state !== this.cognitoOAuthState.value) {
				throw new SyntaxError("Incorrect state");
			}

			const { cognito } = await this.getClients();
			await cognito.loginWithProvider(code, redirectUri);

			const { graphQL } = await this.getClients(); // Gets updated GraphQL client with logged in information.
			const session = await cognito.getSession();
			if (!session) {
				throw new Error("No session after login with provider");
			}
			const { email, name } = session.payload;
			if (typeof email !== "string" || typeof name !== "string") {
				throw new Error("Email or name missing in token payload");
			}
			const me = await graphQL.query<Me, { input: CustomerSignInWithGoogleInput }>({
				query: signInWithGoogle,
				variables: {
					input: {
						...input,
						email,
						fullName: name,
					},
				},
			});
			if (!me) {
				throw new Error("Customer account not found after login with provider");
			}
			await this.setToMe(me);
			broadcastChannel.postMessage({
				kind: "success",
				state,
			});
		} catch (error) {
			Sentry.captureException(error);
			if (state) {
				broadcastChannel.postMessage({
					kind: "error",
					state,
					error: error instanceof Error ? error : new Error(String(error)),
				});
			}
		}
	}

	public async signIn(email: string, password: string): Promise<LoginResponse> {
		const { cognito, graphQL } = await this.getClients();
		const isCustomerMigrated = await graphQL.query<boolean, { email: string }>({
			query: isCustomerMigratedQuery,
			variables: { email },
		});
		if (isCustomerMigrated) {
			return {
				SignedIn: false,
				Challenge: "LEGACY_CUSTOMER",
			};
		}
		const response = await cognito.login(email.toLowerCase(), password);

		if (response.SignedIn) {
			const { graphQL } = await this.getClients(); // Gets updated GraphQL client with logged in information.
			const me = await graphQL.query<Me>({ query: getMe });
			if (!me) {
				throw new Error("Customer not found after successful login");
			}

			await this.setToMe(me);
			this.reloadAfterSignIn();
		}

		return response;
	}

	/** Redirect to show the correct page, which is influenced by CloudFront based on the customer's data. */
	private reloadAfterSignIn(): void {
		if (!this.globalTemplateProps) {
			throw new Error("Auth is not initialized.");
		}
		const targetLocale = this.savedCustomer.value?.locale ?? this.globalTemplateProps.tenant.locale;
		const targetPath = this.globalTemplateProps.pagePath[targetLocale];
		if (targetPath) {
			location.replace(targetPath);
		} else {
			const url = new URL(location.toString());
			url.searchParams.delete("modal");
			url.hash = "";
			location.replace(url);
		}
	}

	public async signInWithNewPassword(email: string, password: string, newPassword: string): Promise<LoginResponse> {
		const { cognito } = await this.getClients();
		const { Session } = await cognito.login(email.toLowerCase(), password);

		if (!Session) {
			throw new Error("No session returned for new password challenge");
		}

		await cognito.confirmNewPassword(Session, email.toLowerCase(), newPassword);
		return await this.signIn(email, newPassword);
	}

	public async signOut(): Promise<void> {
		const { cognito } = await this.getClients();
		await cognito.logout();
		await this.setToMe(null);
		location.reload(); // Here we redirect to show the correct page, which is influenced by CloudFront based on the customer's data.
	}

	public async editMe(input: Partial<CustomerEditMeInput>): Promise<void> {
		const { graphQL } = await this.getClients();
		if (!this.loggedCustomer) {
			throw new Error("No logged customer");
		}

		const completeInput: Complete<CustomerEditMeInput> = {
			deliveryAddresses: this.loggedCustomer.deliveryAddresses,
			billingAddress: this.loggedCustomer.billingAddress,
			company: this.loggedCustomer.company,
			fullName: this.loggedCustomer.fullName,
			locale: this.loggedCustomer.locale,
			phoneNumber: this.loggedCustomer.phoneNumber,
			invoiceEmail: this.loggedCustomer.invoiceEmail,
			...input,
		};

		const me = await graphQL.mutate<{ input: CustomerEditMeInput }, Customer>({
			mutation: editMeMutation,
			variables: { input: completeInput },
		});

		await this.setToCustomer(me);
	}

	public async forgotPassword(locale: Locale, email: string): Promise<void> {
		const { graphQL } = await this.getClients();

		const isEmailVerified = await graphQL.mutate<{ email: string; locale: Locale }, boolean>({
			mutation: forgotPasswordMutation,
			variables: { email, locale },
		});

		if (isEmailVerified) {
			await this.openAuthenticationModal(AuthenticationModalKind.PasswordReset, { emailForReset: email });
		} else {
			await this.openAuthenticationModal(AuthenticationModalKind.ConfirmSignUp, { email });
		}
	}

	public async changePassword(previousPassword: string, proposedPassword: string): Promise<LoginResponse> {
		if (!this.loggedCustomer) {
			throw new Error("No logged customer");
		}
		const { graphQL, cognito } = await this.getClients();
		await cognito.getSession();
		if (!cognito.accessToken) {
			throw new Error("No access token");
		}
		const success = await graphQL.mutate<
			{ previousPassword: string; proposedPassword: string; accessToken: string },
			boolean
		>({
			mutation: changePasswordMutation,
			variables: { previousPassword, proposedPassword, accessToken: cognito.accessToken },
		});
		if (!success) {
			throw new Error("Fatal error");
		}
		return await this.signIn(this.loggedCustomer.email, proposedPassword);
	}

	public async confirmForgotPassword(
		locale: Locale,
		email: string,
		confirmationCode: string,
		newPassword: string,
	): Promise<LoginResponse> {
		const { graphQL } = await this.getClients();
		const result = await graphQL.mutate<
			{ email: string; confirmationCode: string; newPassword: string; locale: Locale },
			boolean
		>({
			mutation: confirmForgotPasswordMutation,
			variables: { email, confirmationCode, newPassword, locale },
		});

		if (!result) {
			throw new Error("Fatal error");
		}

		return await this.signIn(email, newPassword);
	}

	public async confirmSignUp(locale: Locale, email: string, confirmationCode: string): Promise<boolean> {
		const { graphQL } = await this.getClients();
		const result = await graphQL.mutate<{ email: string; confirmationCode: string; locale: Locale }, boolean>({
			mutation: confirmSignUpMutation,
			variables: { email, confirmationCode, locale },
		});

		return result;
	}

	public async resendConfirmationCode(locale: Locale, email: string): Promise<boolean> {
		const { graphQL } = await this.getClients();
		const result = await graphQL.mutate<{ email: string; locale: Locale }, boolean>({
			mutation: resendConfirmationCodeMutation,
			variables: { email, locale },
		});

		return result;
	}

	public async signUp(input: CustomerCreateB2CInput): Promise<void> {
		const { graphQL } = await this.getClients();

		await graphQL.mutate<{ input: CustomerCreateB2CInput }, Customer>({
			mutation: signUp,
			variables: { input },
		});
	}
}

// Cannot be a fullly static class, or a plain object, due to the Svelte Compilation rules:
// https://svelte.dev/docs/svelte/$state#Classes
// Plain object / fully static class: fields not reactive, must implement getters / setters.
// Deeply reactive state: helpful, unless you want $derived, or methods. Also equation will cause trouble because of the proxies.
export const Auth = new WebGlobalAuthentication();
